import { useState } from 'react';
import { VStack, Text, Heading } from 'native-base';

// Components
import {
  PrimaryButton,
  ModalWrapper,
  TextField,
  LocalComponentError,
} from '../../';

// Redux
import { useDispatch } from 'react-redux';
import { openModal } from '@breakroom-nx/redux';

import * as Yup from 'yup';

import { Formik, ErrorMessage } from 'formik';

// Assets
import { theme } from '@breakroom-nx/assets';

import { InputType, Status } from '@breakroom-nx/helpers';
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';

const PasswordChangeSchema = Yup.object().shape({
  password: Yup.string().required('Please enter your password'),
});

export const ChangePassword = () => {
  const initialValues = { password: '' };
  const auth = getAuth();

  const dispatch = useDispatch();
  const [changing, setChanging] = useState<boolean>(false);
  const [textError, setTextError] = useState<string | undefined>(undefined);
  const [step, setStep] = useState<'reauthenticate' | 'updatePassword'>(
    'reauthenticate'
  );

  return (
    <ModalWrapper>
      <VStack
        bg={'surface.100'}
        space={3}
        textAlign={'center'}
        style={{
          borderColor: theme.colors.surface[200],
          borderWidth: 1,
          borderRadius: 25,
          shadowColor: theme.colors.surface[100],
          shadowOpacity: 0.25,
          shadowRadius: 20,
          shadowOffset: {
            width: 10,
            height: 20,
          },
          padding: 30,
        }}
      >
        <VStack space={'5'}>
          <VStack space={4}>
            <LocalComponentError error={textError} />
            <Heading fontSize={30} lineHeight={34}>
              <Text color={'primary.400'}>Update Password</Text>
            </Heading>

            <Text
              fontFamily={'heading'}
              fontSize={16}
              color={'surface.500'}
              fontWeight={600}
            >
              {step === 'reauthenticate' ? (
                <>
                  Please enter your{' '}
                  <Text color={theme.colors.yellow[300]}>current</Text> password
                  to continue.
                </>
              ) : (
                <>
                  Please enter your{' '}
                  <Text color={theme.colors.yellow[300]}>new</Text> password to
                  update it.
                </>
              )}
            </Text>

            <Formik
              initialValues={initialValues}
              validationSchema={PasswordChangeSchema}
              onSubmit={async (values, { resetForm }) => {
                setChanging(true);
                const user = auth.currentUser;

                try {
                  if (!user || !user.email)
                    throw new Error('Auth credentials do not exist');

                  if (step === 'reauthenticate') {
                    // Reauthenticate the user
                    const credential = EmailAuthProvider.credential(
                      user.email,
                      values.password
                    );

                    await reauthenticateWithCredential(user, credential)
                      .then(() => {
                        // console.log('Reauthentication successful');
                        setStep('updatePassword'); // Move to the next step
                        resetForm(); // Clear the password field
                      })
                      .catch(() => {
                        throw new Error(
                          'Please check your current password and try again.'
                        );
                      });
                  } else if (step === 'updatePassword') {
                    // Update the user's password
                    await updatePassword(user, values.password)
                      .then(() => {
                        // console.log('Password updated successfully');
                        dispatch(
                          openModal({
                            modalType: 'Alert',
                            modalProps: {
                              status: Status.success,
                              title: 'Your password has been updated!',
                            },
                          })
                        );
                      })
                      .catch(() => {
                        throw new Error(
                          'Error with updating password, please try again.'
                        );
                      });
                  }
                } catch (err: unknown) {
                  const error = err as Error;
                  setTextError(error.message);
                } finally {
                  setChanging(false);
                }
              }}
            >
              {({ values, handleChange, handleBlur, handleSubmit }) => (
                <VStack width={'full'} space={5}>
                  <TextField
                    disabled={changing}
                    type={InputType.Password}
                    value={values.password}
                    onChangeText={handleChange('password')}
                    onBlur={handleBlur('password')}
                    onSubmitEditing={handleSubmit}
                    placeholder={
                      step === 'reauthenticate'
                        ? 'Current Password'
                        : 'New Password'
                    }
                    autoCompleteType={'password'}
                  />
                  <Text variant={'error'}>
                    <ErrorMessage name="password" />
                  </Text>
                  <PrimaryButton
                    disabled={changing}
                    isLoading={changing}
                    text={step === 'reauthenticate' ? 'Next' : 'Update'}
                    bg={'primary.400'}
                    onPress={handleSubmit}
                  />
                </VStack>
              )}
            </Formik>
          </VStack>
        </VStack>
      </VStack>
    </ModalWrapper>
  );
};
